<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import Draggable from 'vuedraggable';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import ContactInfoRow from './ContactInfoRow.vue';

const ORDER_KEY = 'conversation_elements_order_contact_card';

const props = defineProps({
  contactId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['updateField']);

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const dragging = ref(false);
const displayedElements = ref([]);

const contact = computed(() =>
  getters['contacts/getContact'].value(props.contactId)
);

const attributeDefinitions = computed(() =>
  getters['attributes/getAttributesByModel'].value('contact_attribute')
);

const customAttributes = computed(() => contact.value?.custom_attributes || {});

const getRowIcon = attributeKey => {
  if (attributeKey.includes('id')) {
    return { icon: 'contact-identify', emoji: '🪪' };
  }
  if (attributeKey.includes('empresa') || attributeKey.includes('company')) {
    return { icon: 'building-bank', emoji: '🏢' };
  }
  return { icon: 'contact-identify', emoji: '📋' };
};

const formatDisplayValue = value => {
  if (value === null || value === undefined || value === '') return '';
  return String(value);
};

const staticElements = computed(() => [
  {
    key: 'static-email',
    type: 'static_field',
    field: 'email',
    href: contact.value?.email ? `mailto:${contact.value.email}` : '',
    value: contact.value?.email || '',
    icon: 'mail',
    emoji: '✉️',
    title: t('CONTACT_PANEL.EMAIL_ADDRESS'),
    showCopy: true,
    editable: true,
  },
  {
    key: 'static-phone',
    type: 'static_field',
    field: 'phone_number',
    href: contact.value?.phone_number
      ? `tel:${contact.value.phone_number}`
      : '',
    value: contact.value?.phone_number || '',
    icon: 'call',
    emoji: '📞',
    title: t('CONTACT_PANEL.PHONE_NUMBER'),
    showCopy: true,
    editable: true,
  },
]);

const customAttributeElements = computed(() =>
  attributeDefinitions.value
    .filter(definition => definition.show_in_contact_card)
    .map(definition => {
      const key = definition.attribute_key;
      const hasValue = key in customAttributes.value;
      const rowIcon = getRowIcon(key);

      return {
        ...definition,
        key,
        type: 'custom_attribute',
        value: hasValue ? customAttributes.value[key] : '',
        rowIcon,
      };
    })
);

const allElements = computed(() => [
  ...staticElements.value,
  ...customAttributeElements.value,
]);

const sortElements = (elements, savedOrder = []) => {
  if (!savedOrder.length) return elements;

  return [...elements].sort((a, b) => {
    const aPosition = savedOrder.indexOf(a.key);
    const bPosition = savedOrder.indexOf(b.key);

    if (aPosition === -1 && bPosition === -1) return 0;
    if (aPosition === -1) return 1;
    if (bPosition === -1) return -1;

    return aPosition - bPosition;
  });
};

const syncDisplayedElements = () => {
  const savedOrder = uiSettings.value[ORDER_KEY] ?? [];
  displayedElements.value = sortElements(allElements.value, savedOrder);
};

const reorderElementsWithStaticPreservation = (
  savedOrder = [],
  currentOrder = []
) => {
  const finalOrder = [...currentOrder];
  const visibleKeys = new Set(currentOrder);

  savedOrder
    .filter(key => key.startsWith('static-') && !visibleKeys.has(key))
    .forEach(staticKey => {
      const nextVisible = savedOrder
        .slice(savedOrder.indexOf(staticKey))
        .find(key => visibleKeys.has(key));

      if (nextVisible) {
        finalOrder.splice(finalOrder.indexOf(nextVisible), 0, staticKey);
      } else {
        finalOrder.push(staticKey);
      }
    });

  return finalOrder;
};

const onDragEnd = () => {
  dragging.value = false;
  const savedOrder = uiSettings.value[ORDER_KEY] ?? [];
  const currentOrder = displayedElements.value.map(({ key }) => key);
  const finalOrder = reorderElementsWithStaticPreservation(
    savedOrder,
    currentOrder
  );

  updateUISettings({
    [ORDER_KEY]: finalOrder,
  });
};

const initializeSettings = () => {
  if (!uiSettings.value[ORDER_KEY]?.length) {
    updateUISettings({
      [ORDER_KEY]: allElements.value.map(element => element.key),
    });
  }
  syncDisplayedElements();
};

const onCustomAttributeUpdate = async (key, value) => {
  const updatedAttributes = { ...customAttributes.value, [key]: value };
  try {
    await store.dispatch('contacts/update', {
      id: props.contactId,
      customAttributes: updatedAttributes,
    });
    useAlert(t('CUSTOM_ATTRIBUTES.FORM.UPDATE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('CUSTOM_ATTRIBUTES.FORM.UPDATE.ERROR');
    useAlert(errorMessage);
  }
};

onMounted(() => {
  initializeSettings();
});

watch(allElements, () => {
  syncDisplayedElements();
});
</script>

<template>
  <Draggable
    :list="displayedElements"
    animation="200"
    ghost-class="ghost"
    handle=".drag-handle"
    item-key="key"
    class="flex flex-col gap-2"
    @start="dragging = true"
    @end="onDragEnd"
  >
    <template #item="{ element }">
      <div class="drag-handle cursor-grab active:cursor-grabbing">
        <ContactInfoRow
          v-if="element.type === 'static_field'"
          :href="element.href"
          :value="element.value"
          :icon="element.icon"
          :emoji="element.emoji"
          :title="element.title"
          :show-copy="element.showCopy"
          :editable="element.editable"
          @update="value => emit('updateField', element.field, value)"
        />
        <ContactInfoRow
          v-else
          :value="formatDisplayValue(element.value)"
          :icon="element.rowIcon.icon"
          :emoji="element.rowIcon.emoji"
          :title="element.attribute_display_name"
          show-copy
          editable
          @update="value => onCustomAttributeUpdate(element.attribute_key, value)"
        />
      </div>
    </template>
  </Draggable>
</template>

<style scoped>
.ghost {
  @apply opacity-50 bg-n-slate-3 dark:bg-n-slate-9;
}
</style>
